import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Breadcrumbs, Loader } from '@cloudflare/kumo'
import { HouseIcon, BuildingsIcon } from '@phosphor-icons/react'
import { apiRequest } from '../api/client'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import { BookingFormCore } from '../components/booking-form/booking-form-core'
import type { Building } from '@hub-wifi/shared'

export function BookingFormPage() {
  const { id, bookingId } = useParams<{ id: string; bookingId?: string }>()
  const isEdit = Boolean(bookingId)
  const navigate = useNavigate()
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()

  const [building, setBuilding] = useState<Building | null>(null)
  const [buildingLoading, setBuildingLoading] = useState(true)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    async function loadBuilding() {
      try {
        const token = await getAccessTokenSilently()
        const b = await apiRequest<Building>(`/api/buildings/${id}`, { token })
        if (!cancelled) setBuilding(b)
      } catch {
        // Breadcrumb label only; form has its own error surface.
      } finally {
        if (!cancelled) setBuildingLoading(false)
      }
    }
    void loadBuilding()
    return () => {
      cancelled = true
    }
  }, [id, getAccessTokenSilently])

  if (!id) return null

  if (buildingLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader size="lg" />
      </div>
    )
  }

  const backToList = () => navigate(`/buildings/${id}/bookings`)

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Link icon={<BuildingsIcon size={14} />} href="/buildings">
              {t('buildings.title')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Link href={`/buildings/${id}/bookings`}>
              {building?.name ?? '...'}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>
              {isEdit ? t('bookings.editBooking') : t('bookings.newBooking')}
            </Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={isEdit ? t('bookings.editBooking') : t('bookings.newBooking')}
        description={t('bookings.pageDescription')}
      />

      <BookingFormCore
        bookingId={bookingId}
        scope={{ kind: 'admin', buildingId: id }}
        onSuccess={backToList}
        onCancel={backToList}
      />
    </div>
  )
}
